import React, { useMemo } from 'react';
import {
    closestCenter,
    DndContext,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import TableCell from '@mui/material/TableCell';
import propTypes from 'prop-types';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import SortableOverlay from './SortableOverlay';

export default function SortableList(props) {
    const {
        items,
        onChange,
        renderItem,
        rearrangeColumns,
        setActiveObject,
        activeObject,
        isResizing,
        rowActions,
        userPermission,
    } = props;

    const activeElement = useMemo(
        () => items.find((item) => item.id === activeObject?.id),
        [items, activeObject?.id],
    );

    const activeIndex = items.findIndex((i) => i.id === activeObject?.id);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    return (
        <DndContext
            collisionDetection={closestCenter}
            sensors={sensors}
            onDragStart={({ active }) => {
                if (!isResizing) {
                    document.body.style.setProperty('cursor', 'grab', 'important');
                    setActiveObject(active);
                }
            }}
            onDragEnd={({ active, over }) => {
                if (!isResizing && over && active.id !== over.id) {
                    const dragActiveIndex = items.findIndex(({ id }) => id === active.id);
                    const overIndex = items.findIndex(({ id }) => id === over.id);
                    const newItem = arrayMove(items, dragActiveIndex, overIndex);
                    onChange(newItem);
                    if (typeof rearrangeColumns === 'function') {
                        rearrangeColumns(newItem);
                    }
                }
                document.body.style.removeProperty('cursor');
                setActiveObject(null);
            }}
            modifiers={[restrictToHorizontalAxis]}
            onDragCancel={() => {
                document.body.style.removeProperty('cursor');
                setActiveObject(null);
            }}
        >
            <SortableContext
                items={items.map(item => item.id)}
                strategy={horizontalListSortingStrategy}
            >
                {items.filter(item => item.show).map((item) => {
                    const originalIndex = items.findIndex(({ id }) => id === item.id);
                    return renderItem(item, originalIndex, false);
                })}
                {rowActions && userPermission === 'Modify' &&
                    <TableCell>
                    Action
                    </TableCell>
                }
            </SortableContext>
            <SortableOverlay>
                {activeElement ? renderItem(activeElement, activeIndex, true) : null}
            </SortableOverlay>
        </DndContext>
    );
}

SortableList.propTypes = {
    items: propTypes.arrayOf(
        propTypes.shape({
            id: propTypes.oneOfType([propTypes.string, propTypes.number]).isRequired,
        }),
    ).isRequired,
    onChange: propTypes.func.isRequired,
    renderItem: propTypes.func.isRequired,
    rearrangeColumns: propTypes.func.isRequired,
    setActiveObject: propTypes.func.isRequired,
    activeObject: propTypes.shape({
        id: propTypes.oneOfType([propTypes.string, propTypes.number]),
    }),
    isResizing: propTypes.bool,
    rowActions: propTypes.arrayOf(
        propTypes.shape({
            label: propTypes.string.isRequired,
            clickAction: propTypes.func.isRequired,
        }),
    ),
    userPermission: propTypes.string,
};

